// src/components/ReportModal.jsx - Version corrigée
import React, { useState } from 'react';
import { X, FileText, Download, Eye, Settings, CheckCircle, AlertCircle } from 'lucide-react';

const ReportModal = ({ isOpen, onClose, candidates }) => {
  const [reportOptions, setReportOptions] = useState({
    includeScores: true,
    includeRecommendations: true,
    includeDetails: true,
    includeCharts: true,
    format: 'detailed'
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);

  if (!isOpen) return null;

  const generatePDFContent = () => {
    const stats = {
      total: candidates.length,
      excellent: candidates.filter(c => (c.score || 0) >= 80).length,
      good: candidates.filter(c => (c.score || 0) >= 60 && (c.score || 0) < 80).length,
      average: candidates.filter(c => (c.score || 0) >= 40 && (c.score || 0) < 60).length,
      poor: candidates.filter(c => (c.score || 0) < 40).length,
      averageScore: candidates.length > 0 ? Math.round(candidates.reduce((sum, c) => sum + (c.score || 0), 0) / candidates.length) : 0
    };

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Rapport de Screening IA</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #4F46E5; padding-bottom: 20px; }
        .logo { font-size: 24px; font-weight: bold; color: #4F46E5; margin-bottom: 10px; }
        .title { font-size: 28px; font-weight: bold; margin-bottom: 10px; }
        .subtitle { color: #666; font-size: 16px; }
        .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; }
        .stat-card { background: #F8FAFC; padding: 20px; border-radius: 8px; text-align: center; border: 1px solid #E2E8F0; }
        .stat-number { font-size: 32px; font-weight: bold; color: #4F46E5; }
        .stat-label { color: #666; font-size: 14px; margin-top: 5px; }
        .candidate { margin: 20px 0; padding: 20px; border: 1px solid #E2E8F0; border-radius: 8px; }
        .candidate-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
        .candidate-name { font-size: 18px; font-weight: bold; }
        .candidate-score { font-size: 24px; font-weight: bold; }
        .score-excellent { color: #10B981; }
        .score-good { color: #3B82F6; }
        .score-average { color: #F59E0B; }
        .score-poor { color: #EF4444; }
        .candidate-details { margin-top: 10px; }
        .detail-row { margin: 5px 0; }
        .label { font-weight: bold; color: #374151; }
        .recommendation { background: #EFF6FF; padding: 15px; border-radius: 6px; margin-top: 15px; }
        .page-break { page-break-before: always; }
        .footer { text-align: center; margin-top: 40px; color: #666; font-size: 12px; border-top: 1px solid #E2E8F0; padding-top: 20px; }
    </style>
</head>
<body>
    <div class="header">
        <div class="logo">🧠 CV Screening Pro</div>
        <div class="title">Rapport d'Analyse IA</div>
        <div class="subtitle">Généré le ${new Date().toLocaleDateString('fr-FR')} • ${candidates.length} candidats analysés</div>
    </div>

    <div class="stats">
        <div class="stat-card">
            <div class="stat-number">${stats.total}</div>
            <div class="stat-label">Total Candidats</div>
        </div>
        <div class="stat-card">
            <div class="stat-number">${stats.excellent}</div>
            <div class="stat-label">Excellents (80%+)</div>
        </div>
        <div class="stat-card">
            <div class="stat-number">${stats.averageScore}%</div>
            <div class="stat-label">Score Moyen</div>
        </div>
    </div>

    ${candidates.map((candidate, index) => `
        <div class="candidate">
            <div class="candidate-header">
                <div>
                    <div class="candidate-name">#${index + 1} ${candidate.prenom || 'Prénom'} ${candidate.nom || 'Nom'}</div>
                    <div style="color: #666; font-size: 14px;">${candidate.fileName}</div>
                </div>
                <div class="candidate-score ${
                    (candidate.score || 0) >= 80 ? 'score-excellent' :
                    (candidate.score || 0) >= 60 ? 'score-good' :
                    (candidate.score || 0) >= 40 ? 'score-average' : 'score-poor'
                }">${candidate.score || 0}%</div>
            </div>
            
            ${reportOptions.includeDetails ? `
            <div class="candidate-details">
                <div class="detail-row"><span class="label">Email:</span> ${candidate.email || 'Non renseigné'}</div>
                <div class="detail-row"><span class="label">Téléphone:</span> ${candidate.telephone || 'Non renseigné'}</div>
                <div class="detail-row"><span class="label">Expérience:</span> ${candidate.experience_totale || 0} ans</div>
                <div class="detail-row"><span class="label">Diplôme:</span> ${candidate.diplome_plus_haut || 'Non spécifié'}</div>
                <div class="detail-row"><span class="label">Entreprises:</span> ${candidate.entreprises?.join(', ') || 'Non mentionnées'}</div>
            </div>
            ` : ''}
            
            ${reportOptions.includeRecommendations ? `
            <div class="recommendation">
                <strong>🤖 Analyse IA:</strong><br>
                ${candidate.avis_expert_rh || candidate.analyse_correspondance || 'Analyse non disponible'}<br><br>
                <strong>💡 Recommandation:</strong> ${candidate.recommandation_finale || 'En évaluation'}
            </div>
            ` : ''}
        </div>
    `).join('')}

    <div class="footer">
        Rapport généré par CV Screening Pro avec Claude Sonnet 4 • Confidentiel
    </div>
</body>
</html>`;
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      // Générer le contenu HTML
      const htmlContent = generatePDFContent();
      
      // Créer un Blob avec le contenu HTML
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      
      // Créer l'URL de téléchargement
      const url = URL.createObjectURL(blob);
      
      // Créer le lien de téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-screening-${new Date().toISOString().split('T')[0]}.html`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Nettoyer l'URL
      URL.revokeObjectURL(url);
      
      setReportGenerated(true);
      
      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setReportGenerated(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Erreur génération rapport:', error);
      alert('Erreur lors de la génération du rapport');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl mx-4 bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl">
        
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">📄 Génération Rapport HTML</h3>
              <p className="text-gray-400 text-sm">Rapport détaillé de {candidates.length} candidats</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {reportGenerated ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-white mb-2">✅ Rapport généré avec succès !</h4>
              <p className="text-gray-400">Le fichier HTML a été téléchargé. Vous pouvez l'ouvrir dans votre navigateur.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="text-white font-semibold mb-4">Options du rapport</h4>
                <div className="space-y-3">
                  {[
                    { key: 'includeScores', label: 'Inclure les scores et classements', icon: '📊' },
                    { key: 'includeRecommendations', label: 'Inclure les recommandations IA', icon: '🤖' },
                    { key: 'includeDetails', label: 'Inclure les détails candidats', icon: '👤' },
                    { key: 'includeCharts', label: 'Inclure les statistiques', icon: '📈' }
                  ].map((option) => (
                    <label key={option.key} className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportOptions[option.key]}
                        onChange={(e) => setReportOptions(prev => ({
                          ...prev,
                          [option.key]: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-lg">{option.icon}</span>
                      <span className="text-white">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {isGenerating && (
                <div className="p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg text-center">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-blue-300 font-medium">Génération du rapport en cours...</p>
                  <p className="text-blue-400 text-sm mt-1">Compilation des données et mise en forme</p>
                </div>
              )}

              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <h6 className="text-yellow-300 font-medium mb-1">📋 Format HTML</h6>
                    <p className="text-yellow-200 text-sm">
                      Le rapport sera généré en format HTML pour un affichage optimal. Vous pourrez l'imprimer en PDF depuis votre navigateur.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-gray-400 text-sm">
            Format HTML avec design professionnel
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white transition-all"
            >
              Annuler
            </button>
            {!reportGenerated && (
              <button
                onClick={handleGenerateReport}
                disabled={isGenerating}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
              >
                <Download className="w-4 h-4" />
                <span>{isGenerating ? 'Génération...' : 'Générer Rapport'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;